import { TFunction } from 'i18next';

import { ClientConfiguration, Permission } from '@Models';
import { SidebarIcons } from '../icons';
import { MenuItem } from '../types';
import { buildDocumentationItems, buildTrainingVideosItem, resolveDataRepositoryEntries } from './documentation';

/**
 * GS1 Mexico flavor menu.
 *
 * Sections: Configuration (Credentials) → Admin (Suppliers, Users)
 * → Tools (external links) → Toolkit (docs + videos, conditional).
 *
 * The Configuration / Masters entry is shown only when the account has
 * configuration_links set up (isMastersEnabled). Accounts without links
 * (typically Suppliers) keep the tab hidden; those with links see it.
 */
const buildGs1Menu = (configuration: ClientConfiguration | undefined, t: TFunction): MenuItem[] => {
  const dataRepositoryEntries = resolveDataRepositoryEntries(configuration?.dataRepository);

  const credentialsItem: MenuItem | null =
    configuration?.connectorTypes && !configuration?.hasMultipleAccounts
      ? {
          key: 'credentials',
          label: t('Sidebar.Chains'),
          icon: SidebarIcons.credentials,
          link: '/credentials',
          enabled: true,
        }
      : null;

  const items: (MenuItem | null)[] = [
    {
      key: 'configuration',
      label: t('Sidebar.Configuration'),
      icon: SidebarIcons.configuration,
      submenu: [
        ...(credentialsItem ? [credentialsItem] : []),
        ...(configuration?.isMastersEnabled
          ? [
              {
                key: 'masters',
                label: t('Sidebar.Master'),
                icon: SidebarIcons.file,
                link: '/configuration/masters',
                enabled: true,
              },
            ]
          : []),
      ],
    },
    {
      key: 'admin',
      label: 'Admin',
      icon: SidebarIcons.admin,
      submenu: [
        {
          key: 'suppliers',
          permission: Permission.ManageSuppliers,
          label: t('Sidebar.Suppliers'),
          icon: SidebarIcons.suppliers,
          link: '/suppliers',
          hideWhenDisabled: true,
          enabled: true,
        },
        {
          key: 'credentials-summary',
          permission: [Permission.ViewSuppliers, Permission.ManageSuppliers],
          label: t('Sidebar.CredentialsSummary'),
          icon: SidebarIcons.credentials,
          link: '/credentials-summary',
          hideWhenDisabled: true,
          enabled: true,
        },
        {
          key: 'users',
          permission: Permission.ManageUsers,
          label: t('Sidebar.Users'),
          icon: SidebarIcons.users,
          link: '/users',
          hideWhenDisabled: true,
          enabled: true,
        },
      ],
    },
    configuration?.isTopicEnabled
      ? {
          key: 'ask-bops',
          label: t('Sidebar.AskBops'),
          icon: SidebarIcons.agent,
          link: '/ask',
          enabled: true,
        }
      : null,
    {
      key: 'tools',
      label: t('Sidebar.Tools'),
      icon: SidebarIcons.tools,
      submenu: [
        { key: 'gpc', label: t('Sidebar.Gpc'), externalLink: 'https://gpc-browser.gs1.org', enabled: true },
        {
          key: 'syncfonia',
          label: t('Sidebar.Syncfonia'),
          externalLink: 'https://gs1mexicoprod.syndigo.com/',
          enabled: true,
        },
        {
          key: 'verified',
          label: t('Sidebar.Verified'),
          externalLink: 'https://www.gs1mexico.org/verified',
          enabled: true,
        },
      ],
    },
  ];

  if (dataRepositoryEntries.length > 0 || configuration?.resources) {
    items.push({
      key: 'toolkit',
      label: t('Sidebar.Toolkit'),
      icon: SidebarIcons.toolkit,
      submenu: [
        ...buildDocumentationItems(configuration?.dataRepository, t),
        ...buildTrainingVideosItem(configuration?.resources, t),
      ],
    });
  }

  return items.filter((item): item is MenuItem => item !== null);
};

export default buildGs1Menu;
